import type { ConsumerProductKind } from '@/components/value-chain/starlinkConsumer';

export function ConsumerProductArt({ kind }: { kind: ConsumerProductKind }) {
  if (kind === 'standard-4x') return <StandardDishArt tilt={-18} artId="4x" />;
  if (kind === 'standard-4') return <StandardDishArt tilt={-8} artId="std4" />;
  if (kind === 'mini') return <MiniDishArt />;
  if (kind === 'router-3') return <RouterArt tall />;
  if (kind === 'router-mini') return <RouterArt />;
  if (kind === 'mounts') return <MountsArt />;
  return <AppArt />;
}

export function MappingProductIcon({ kind }: { kind: ConsumerProductKind }) {
  return (
    <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-slate-100">
      <svg viewBox="0 0 48 48" className="h-8 w-8">
        {kind === 'standard-4x' || kind === 'standard-4' ? (
          <g transform="translate(4,6)">
            <rect x="6" y="4" width="28" height="18" rx="2" fill="#e8eef4" stroke="#94a3b8" strokeWidth="1.2" transform="rotate(-18 20 13)" />
            <path d="M18 22 L22 38 L26 22" fill="#1e293b" />
          </g>
        ) : null}
        {kind === 'mini' ? (
          <g transform="translate(6,12)">
            <rect x="2" y="4" width="32" height="14" rx="2" fill="#e8eef4" stroke="#94a3b8" strokeWidth="1.2" transform="rotate(-6 18 11)" />
            <rect x="16" y="18" width="4" height="10" fill="#1e293b" />
          </g>
        ) : null}
        {kind === 'router-3' || kind === 'router-mini' ? (
          <rect x="16" y={kind === 'router-3' ? 8 : 14} width="16" height={kind === 'router-3' ? 32 : 24} rx="2" fill="#f8fafc" stroke="#cbd5e1" />
        ) : null}
        {kind === 'mounts' ? (
          <g transform="translate(8,10)" stroke="#64748b" fill="none" strokeWidth="1.8">
            <path d="M8 4 h16 v10 H8z" />
            <path d="M16 14 v18" />
            <path d="M10 24 h12" />
          </g>
        ) : null}
        {kind === 'app' ? (
          <g transform="translate(14,6)">
            <rect x="0" y="0" width="20" height="36" rx="3" fill="#0f172a" />
            <path d="M4 14 L16 28 H10 L4 14Z" fill="#fff" />
          </g>
        ) : null}
      </svg>
    </span>
  );
}

function StandardDishArt({ tilt, artId }: { tilt: number; artId: string }) {
  return (
    <svg viewBox="0 0 180 150" className="h-full w-full">
      <defs>
        <linearGradient id={`dishFace-${artId}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="55%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#cbd5e1" />
        </linearGradient>
        <filter id={`soft-${artId}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor="#94a3b8" floodOpacity="0.28" />
        </filter>
      </defs>
      <g transform={`translate(90,78) rotate(${tilt})`}>
        <ellipse cx="0" cy="38" rx="42" ry="7" fill="#94a3b8" opacity="0.22" />
        <path d="M-6 8 L6 8 L10 46 L-10 46Z" fill="#1e293b" />
        <path d="M-22 44 L22 44 L16 58 L-16 58Z" fill="#0f172a" />
        <rect
          x="-54"
          y="-38"
          width="108"
          height="70"
          rx="6"
          fill={`url(#dishFace-${artId})`}
          stroke="#94a3b8"
          strokeWidth="1.4"
          filter={`url(#soft-${artId})`}
        />
        <rect x="-46" y="-30" width="92" height="54" rx="3" fill="none" stroke="#cbd5e1" strokeWidth="0.8" />
        <path d="M-40 -8 H40 M-40 4 H40 M-40 16 H40" stroke="#94a3b8" strokeWidth="0.4" opacity="0.35" />
        <circle cx="0" cy="-4" r="3" fill="#64748b" opacity="0.5" />
      </g>
    </svg>
  );
}

function MiniDishArt() {
  return (
    <svg viewBox="0 0 180 150" className="h-full w-full">
      <defs>
        <linearGradient id="miniFace" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#cbd5e1" />
        </linearGradient>
      </defs>
      <g transform="translate(90,86) rotate(-6)">
        <ellipse cx="0" cy="28" rx="48" ry="6" fill="#94a3b8" opacity="0.2" />
        <rect x="-8" y="8" width="16" height="28" rx="2" fill="#1e293b" />
        <rect x="-58" y="-26" width="116" height="42" rx="5" fill="url(#miniFace)" stroke="#94a3b8" strokeWidth="1.3" />
        <rect x="-50" y="-18" width="100" height="26" rx="2" fill="none" stroke="#cbd5e1" />
      </g>
    </svg>
  );
}

function RouterArt({ tall = false }: { tall?: boolean }) {
  const h = tall ? 96 : 72;
  const y = tall ? 22 : 38;
  return (
    <svg viewBox="0 0 180 150" className="h-full w-full">
      <defs>
        <linearGradient id={`routerFace-${tall ? 't' : 's'}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>
      </defs>
      <ellipse cx="90" cy="128" rx="28" ry="6" fill="#94a3b8" opacity="0.18" />
      <rect x="66" y={y} width="48" height={h} rx="6" fill={`url(#routerFace-${tall ? 't' : 's'})`} stroke="#cbd5e1" />
      <rect x="72" y={y + 8} width="36" height={tall ? 14 : 10} rx="2" fill="#f1f5f9" />
      <circle cx="90" cy={y + h - 12} r="3" fill="#86efac" />
      <path d={`M74 ${y + 28} H106`} stroke="#e2e8f0" strokeWidth="2" />
    </svg>
  );
}

function MountsArt() {
  return (
    <svg viewBox="0 0 180 150" className="h-full w-full">
      <g transform="translate(28,36)" fill="none" stroke="#64748b" strokeWidth="3" strokeLinecap="round">
        <rect x="8" y="8" width="36" height="22" rx="3" fill="#e2e8f0" stroke="#94a3b8" />
        <path d="M26 30 v48" />
        <path d="M16 54 h20" />
        <circle cx="26" cy="86" r="8" fill="#cbd5e1" />
      </g>
      <g transform="translate(96,42)" fill="none" stroke="#64748b" strokeWidth="3" strokeLinecap="round">
        <path d="M8 8 h40 v16 H8z" fill="#e2e8f0" stroke="#94a3b8" />
        <path d="M48 16 h22" />
        <path d="M70 8 v40" />
        <rect x="62" y="48" width="16" height="28" rx="2" fill="#cbd5e1" stroke="#94a3b8" />
      </g>
    </svg>
  );
}

function AppArt() {
  return (
    <svg viewBox="0 0 180 150" className="h-full w-full">
      <rect x="64" y="14" width="52" height="112" rx="10" fill="#0f172a" />
      <rect x="68" y="22" width="44" height="96" rx="4" fill="#020617" />
      <path d="M78 52 L102 88 H90 L78 52Z" fill="#f8fafc" />
      <path d="M92 52 h18 L98 72 H88Z" fill="#f8fafc" />
      <circle cx="90" cy="108" r="1.5" fill="#64748b" />
    </svg>
  );
}

export function CoastalBanner() {
  return (
    <svg viewBox="0 0 320 120" className="h-full w-full">
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="55%" stopColor="#bae6fd" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <linearGradient id="sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0369a1" />
          <stop offset="100%" stopColor="#0c4a6e" />
        </linearGradient>
      </defs>
      <rect width="320" height="120" fill="url(#sky)" />
      <ellipse cx="268" cy="28" rx="18" ry="18" fill="#fef9c3" opacity="0.9" />
      <path d="M0 62 C70 48 140 78 210 58 C250 48 290 62 320 52 V88 H0Z" fill="#d6d3d1" />
      <rect y="86" width="320" height="34" fill="url(#sea)" />
      <path d="M0 92 C40 86 80 98 120 90 C170 80 230 96 320 88 V120 H0Z" fill="#075985" opacity="0.55" />
      <g transform="translate(48,18) rotate(-16)">
        <rect x="0" y="0" width="70" height="42" rx="3" fill="#f8fafc" stroke="#94a3b8" />
        <path d="M28 40 L36 78 L44 40" fill="#1e293b" />
      </g>
    </svg>
  );
}
